// pages/ManifestationDetailPage.tsx
// 新 API (rl_book_id ベース) 移行版

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildBibSimpleUrl, NEW_API, LEGACY_API } from '../config/api';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import IosShareIcon from '@mui/icons-material/IosShare';

import { useAuth } from '../context/AuthContext';
import { BibSimpleResponse, RelatedBookItem, RelatedBooksAuthor } from '../types/rlTypes';
import {
  getBibTitle, getBibIsbn, getBibPublisher, getBibPublishingDate,
  getBibResponsibility, getBibNdlId, getBibContentDetailTitle,
  getBibTitleOthers, getBibContributors, generateCoverUrlFromBib,
  getBibVolume, titleWithVolume,
} from '../utils/bibAdapter';

import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import amazonImage from '../assets/images/assocbtn_gray_amazon_new1.png';
import calilImage from '../assets/images/calil-button-image.png';
import ndlImage from '../assets/images/ndl-button-image.png';
import wikipediaLogo from '../assets/images/wikipedia_logo.png';
import googleSearchIcon from '../assets/images/google-search-icon.png';

import { useFavorites } from '../context/FavoritesContext';
import { fetchFavoriteCount, addFavoriteBook, removeFavoriteBook } from '../utils/favoritesAndHistoryApi';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { Helmet } from 'react-helmet-async';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import Menu from '@mui/material/Menu';
import { fetchLists, createList, addItem, removeItem, fetchListMembership } from '../utils/listHandler';
import type { UserList, ListOpenLevel } from '../types/rlTypes';
import {
  fetchShelves, fetchBookShelfStatus, registerBookToShelf,
} from '../utils/shelfHandler';
import type { ShelfSummary, BookShelfStatus } from '../utils/shelfHandler';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import Snackbar from '@mui/material/Snackbar';
import { useAppTokens } from '../context/ThemeModeContext';

const ManifestationDetailPage: React.FC = () => {
  const { rlBookId } = useParams<{ rlBookId: string }>();
  const rlBookIdNum = rlBookId ? Number(rlBookId) : null;

  const [bibData, setBibData] = useState<BibSimpleResponse | null>(null);
  const [wikipediaLinks, setWikipediaLinks] = useState<{ [key: string]: string | null }>({});
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();
  const { idToken, userSub } = useAuth();

  // NDL リンク用（書誌データから NDL 書誌 ID を取得）
  const ndlBibId = bibData ? getBibNdlId(bibData) : undefined;

  // お気に入り（新 API — rl_book_id ベース）
  const [favoriteCount, setFavoriteCount] = useState<number>(0);
  const { state, dispatch } = useFavorites();
  const isFavorite = rlBookIdNum ? state.favoriteBooks.some(book => book.rl_book_id === rlBookIdNum) : false;

  // 関連書籍
  const [relatedBooks, setRelatedBooks] = useState<RelatedBookItem[]>([]);
  const [relatedAuthors, setRelatedAuthors] = useState<RelatedBooksAuthor[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  // GetRelatedBooksはlimit件で打ち切られるため、上限に達している場合は
  // 「◯件」ではなく「◯件のみ表示中」と明示する（実際はもっと多いのに
  // 全件のように見えてしまうのを防ぐ）
  const [relatedBooksAtLimit, setRelatedBooksAtLimit] = useState(false);
  const RELATED_BOOKS_LIMIT = 20;

  const [bookLists, setBookLists] = useState<UserList[]>([]);
  // この本がすでに入っているリストID（緑チェック表示・トグル判定用）
  const [inListIds, setInListIds] = useState<Set<string>>(new Set());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openCreateListDialog, setOpenCreateListDialog] = useState(false);
  const [newListName, setNewListName] = useState('気になる本');
  const [newListOpenLevel, setNewListOpenLevel] = useState<ListOpenLevel>('private');
  const [isCreatingList, setIsCreatingList] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // 本棚管理
  const [shelfStatus, setShelfStatus] = useState<BookShelfStatus | null>(null);
  const [shelfStatusExpanded, setShelfStatusExpanded] = useState(false);
  const [allShelves, setAllShelves] = useState<ShelfSummary[]>([]);
  const [shelfModalOpen, setShelfModalOpen] = useState(false);
  const [shelfModalStatusExpanded, setShelfModalStatusExpanded] = useState(false);
  const [selectedShelfIdForAdd, setSelectedShelfIdForAdd] = useState('');
  const [addingToShelf, setAddingToShelf] = useState(false);

  // ===== Style tokens =====
  const TOKENS = useAppTokens();

  const ghostButtonSx = {
    color: TOKENS.text,
    background: TOKENS.buttonBg,
    border: `1px solid ${TOKENS.borderSoft}`,
    borderRadius: 999,
    textTransform: 'none',
    fontSize: 12,
    px: 1.25,
    py: 0.75,
    minHeight: 34,
    '&:hover': {
      background: TOKENS.buttonBgHover,
      border: `1px solid ${TOKENS.accent}`,
    },
  } as const;

  const iconGhostButtonSx = {
    color: TOKENS.text,
    background: TOKENS.buttonBg,
    border: `1px solid ${TOKENS.borderSoft}`,
    '&:hover': {
      background: TOKENS.buttonBgHover,
      border: `1px solid ${TOKENS.accent}`,
    },
  } as const;

  // 外部リンクのロゴ画像（Amazon/カーリル/NDL）用。ロゴ自体に既に色/デザインが
  // あるため、通常のボタンのような枠・背景で二重に囲むと違和感がある。
  // 平常時は枠なし、ホバー時だけうっすら枠を出す
  const logoLinkButtonSx = {
    p: 0.5,
    borderRadius: 2,
    minHeight: 'unset',
    minWidth: 'unset',
    background: 'transparent',
    border: '1px solid transparent',
    '&:hover': {
      background: 'transparent',
      border: `1px solid ${TOKENS.borderSoft}`,
    },
  } as const;

  // ISBN-13 → ISBN-10 変換
  const convertISBN13toISBN10 = (isbn: string): string => {
    if (!isbn || isbn.length !== 13 || !isbn.startsWith("978")) {
      throw new Error('ISBN-13形式ではありません');
    }
    const isbn9 = isbn.substring(3, 12);
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += (10 - i) * parseInt(isbn9.charAt(i), 10);
    }
    let checkDigit: number | string = 11 - (sum % 11);
    checkDigit = checkDigit === 10 ? 'X' : checkDigit === 11 ? '0' : checkDigit.toString();
    return isbn9 + checkDigit;
  };

  const createAmazonLink = (isbn: string, mainTitle: string): string => {
    let amazonISBN = isbn;
    if (isbn && isbn.length === 13 && isbn.startsWith("978")) {
      try {
        amazonISBN = convertISBN13toISBN10(isbn);
      } catch (error) {
        console.error((error as Error).message);
      }
    }
    if (amazonISBN && amazonISBN.length === 10) {
      return `http://www.amazon.co.jp/dp/${amazonISBN}?&tag=readlinker202-22&linkCode=ogi`;
    } else {
      const encodedTitle = encodeURIComponent(mainTitle);
      return `https://www.amazon.co.jp/s?k=${encodedTitle}&__mk_ja_JP=%E3%82%AB%E3%82%BF%E3%82%AB%E3%83%8A`;
    }
  };

  const createCalilLink = (isbn: string) => `https://calil.jp/book/${isbn}`;
  const createNDLLink = (ndlId: string) => `https://ndlsearch.ndl.go.jp/books/R100000002-I${ndlId}`;

  // Wikipedia リンク取得
  const fetchAuthorsWikipedia = async (tenkyoIDs: string[]): Promise<{ [key: string]: string | null }> => {
    if (tenkyoIDs.length === 0) return {};
    try {
      const response = await axios.post(LEGACY_API.AUTHORS_INFO, {
        Operation: 'getAuthorsWikipediaByTenkyoIDs',
        TENKYO_IDs: tenkyoIDs,
      }, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 200) {
        return response.data.reduce((acc: { [key: string]: string | null }, item: any) => {
          acc[item.TENKYO_ID!] = item.Wikipedia_Link;
          return acc;
        }, {} as { [key: string]: string | null });
      }
      return {};
    } catch (error) {
      console.error('Error fetching Wikipedia links:', error);
      return {};
    }
  };

  // お気に入りカウント取得（新 API — rl_book_id ベース）
  useEffect(() => {
    const loadFavoriteCount = async () => {
      if (rlBookIdNum) {
        try {
          const res = await fetchFavoriteCount(rlBookIdNum);
          setFavoriteCount(res.data?.count ?? 0);
        } catch (error) {
          console.error('Error fetching favorite count:', error);
        }
      }
    };
    loadFavoriteCount();
  }, [rlBookIdNum]);

  const handleFavoriteToggle = async () => {
    if (!rlBookIdNum || !idToken) return;
    try {
      if (isFavorite) {
        await removeFavoriteBook(rlBookIdNum, idToken);
        dispatch({ type: 'REMOVE_FAVORITE', payload: rlBookIdNum });
        setFavoriteCount(prev => prev - 1);
      } else {
        const res = await addFavoriteBook(rlBookIdNum, idToken);
        dispatch({ type: 'ADD_FAVORITE', payload: { rl_book_id: rlBookIdNum, favorited_at: res.data?.favorited_at } });
        setFavoriteCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleShare = async () => {
    const title = bibData ? titleWithVolume(getBibTitle(bibData), getBibVolume(bibData)) : '';
    try {
      if (navigator.share) {
        await navigator.share({
          title,
          url: `https://readlinker.com/book/${rlBookIdNum}`
        });
      } else {
        navigator.clipboard.writeText(`https://readlinker.com/book/${rlBookIdNum}`);
        alert('URLをコピーしました');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // 書誌情報取得（新 BIB_SIMPLE API）
  useEffect(() => {
    const fetchBookDetail = async () => {
      if (!rlBookIdNum) return;
      setLoading(true);
      try {
        const response = await axios.get(buildBibSimpleUrl(rlBookIdNum));
        setBibData(response.data as BibSimpleResponse);
      } catch (error) {
        console.error('Error fetching book detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookDetail();
  }, [rlBookIdNum]);

  // Wikipedia リンク取得
  useEffect(() => {
    if (!bibData) return;
    const contributors = getBibContributors(bibData);
    const tenkyoIds = contributors
      .map(c => c.tenkyo_id)
      .filter((id): id is string => !!id);

    if (tenkyoIds.length > 0) {
      fetchAuthorsWikipedia(tenkyoIds).then(setWikipediaLinks);
    }
  }, [bibData]);

  // 関連書籍取得（新 API — GetRelatedBooks）
  useEffect(() => {
    const fetchRelatedBooks = async () => {
      if (!rlBookIdNum) return;
      setRelatedLoading(true);
      try {
        const res = await axios.get(NEW_API.GRAPH_ENGINE, {
          params: { operation: 'GetRelatedBooks', rl_book_id: rlBookIdNum, limit: RELATED_BOOKS_LIMIT },
        });
        const data = res.data?.data;
        if (data) {
          const rawBooks = data.related_books ?? [];
          const books = rawBooks.filter(
            (b: RelatedBookItem) => b.rl_book_id !== rlBookIdNum
          );
          setRelatedBooks(books);
          setRelatedAuthors(data.authors ?? []);
          setRelatedBooksAtLimit(rawBooks.length >= RELATED_BOOKS_LIMIT);
        }
      } catch (error) {
        console.error('Error fetching related books:', error);
      } finally {
        setRelatedLoading(false);
      }
    };
    fetchRelatedBooks();
  }, [rlBookIdNum]);

  // リスト管理
  const fetchBookLists = async () => {
    if (idToken) {
      try {
        const res = await fetchLists(idToken);
        const sorted = (res.items ?? []).slice().sort(
          (a, b) => (a.user_list_order ?? 0) - (b.user_list_order ?? 0),
        );
        setBookLists(sorted);
      } catch (error) {
        console.error('Error fetching book lists:', error);
      }
    }
  };

  useEffect(() => { fetchBookLists(); }, [idToken]);

  // この本がどのリストに入っているか（緑チェック表示用）。書誌詳細ページを開くたびに
  // 軽量なAPIで取得し直す（リスト・アイテム数によらず高速なため、永続キャッシュはしない）
  useEffect(() => {
    if (!rlBookIdNum || !idToken) {
      setInListIds(new Set());
      return;
    }
    let cancelled = false;
    fetchListMembership(rlBookIdNum, idToken)
      .then((ids) => { if (!cancelled) setInListIds(ids); })
      .catch((error) => console.error('Error fetching list membership:', error));
    return () => { cancelled = true; };
  }, [rlBookIdNum, idToken]);

  const handleListButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleListClose = () => { setAnchorEl(null); };

  const handleToggleListItem = async (listId: string) => {
    if (!rlBookIdNum || !idToken) return;
    const wasInList = inListIds.has(listId);
    let showSnackbar = true;
    try {
      if (wasInList) {
        await removeItem(listId, rlBookIdNum, idToken);
        setInListIds((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
        setSnackbarMessage('リストから外しました');
      } else {
        await addItem(listId, rlBookIdNum, null, idToken);
        setInListIds((prev) => new Set(prev).add(listId));
        setSnackbarMessage('リストへ追加しました');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      // 409(既に追加済み)・404(既に削除済み)は、表示していたチェック状態が実際の
      // サーバー状態とズレていただけの想定内のケース。ユーザーから見れば「外したい
      // /追加したい」という意図通りの結果になっているだけなので、通知は出さず
      // 表示状態を静かに自己修正するだけに留める（true failureは他のステータスのみ通知）
      if (status === 409) {
        setInListIds((prev) => new Set(prev).add(listId));
        showSnackbar = false;
      } else if (status === 404) {
        setInListIds((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
        showSnackbar = false;
      } else {
        console.error('Error toggling book in list:', error);
        setSnackbarMessage('エラーが発生しました');
      }
    } finally {
      if (showSnackbar) setSnackbarOpen(true);
      handleListClose();
    }
  };

  const handleCreateNewList = () => {
    handleListClose();
    setOpenCreateListDialog(true);
  };

  const handleCloseCreateListDialog = () => {
    setOpenCreateListDialog(false);
    setNewListName('気になる本');
    setNewListOpenLevel('private');
  };

  const handleCreateList = async () => {
    if (!idToken || !userSub) return;
    setIsCreatingList(true);
    try {
      await createList(newListName, null, newListOpenLevel, idToken);
      await fetchBookLists();
      handleCloseCreateListDialog();
    } catch (error) {
      console.error('Error creating new list:', error);
    } finally {
      setIsCreatingList(false);
    }
  };

  const handleSnackbarClose = () => { setSnackbarOpen(false); };

  // 本棚管理
  const fetchShelfStatus = async () => {
    if (!rlBookIdNum || !idToken) {
      setShelfStatus(null);
      return;
    }
    try {
      const status = await fetchBookShelfStatus(rlBookIdNum, idToken);
      setShelfStatus(status);
    } catch (error) {
      console.error('Error fetching shelf status:', error);
    }
  };

  useEffect(() => { fetchShelfStatus(); }, [rlBookIdNum, idToken]);

  const fetchAllShelves = async () => {
    if (!idToken) {
      setAllShelves([]);
      return;
    }
    try {
      const shelves = await fetchShelves(idToken);
      setAllShelves(shelves);
    } catch (error) {
      console.error('Error fetching shelves:', error);
    }
  };

  useEffect(() => { fetchAllShelves(); }, [idToken]);

  const handleOpenShelfModal = () => {
    setSelectedShelfIdForAdd('');
    setShelfModalStatusExpanded(false);
    setShelfModalOpen(true);
  };

  const handleCloseShelfModal = () => {
    if (addingToShelf) return;
    setShelfModalOpen(false);
  };

  const handleAddToShelf = async () => {
    if (!rlBookIdNum || !idToken) return;
    setAddingToShelf(true);
    try {
      await registerBookToShelf(rlBookIdNum, selectedShelfIdForAdd || null, idToken);
      // bulk_register は SQS 経由の非同期処理のため、直後に取得すると反映前のことがある
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await fetchShelfStatus();
      setSnackbarMessage('本棚に追加しました');
      setShelfModalOpen(false);
    } catch (error) {
      console.error('Error adding book to shelf:', error);
      setSnackbarMessage('エラーが発生しました');
    } finally {
      setAddingToShelf(false);
      setSnackbarOpen(true);
    }
  };

  // 表示用データ抽出
  const title = bibData ? titleWithVolume(getBibTitle(bibData), getBibVolume(bibData)) : '';
  const isbn = bibData ? getBibIsbn(bibData) : undefined;
  const publisher = bibData ? getBibPublisher(bibData) : undefined;
  const publishingDate = bibData ? getBibPublishingDate(bibData) : undefined;
  const responsibility = bibData ? getBibResponsibility(bibData) : undefined;
  const contentDetailTitle = bibData ? getBibContentDetailTitle(bibData) : undefined;
  const titleOthers = bibData ? getBibTitleOthers(bibData) : undefined;
  const coverUrl = bibData ? generateCoverUrlFromBib(bibData) : '';
  const contributors = bibData ? getBibContributors(bibData) : [];

  return (
    <Box sx={{ mt: 7 }}>
    <Box
      sx={{
        width: '100%',
        height: { xs: 'auto', md: 'calc(100vh - 56px)' },
        minHeight: { xs: 'calc(100vh - 56px)', md: 'auto' },
        background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
        color: TOKENS.text,
        overflow: { xs: 'visible', md: 'hidden' },
        position: 'relative',
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          height: { xs: 'auto', md: '100%' },
          pt: 3,
          pb: 6,
          px: { xs: 2, md: 3 },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {bibData && (
              <>
                <Helmet>
                  <title>{`ReadLinker | ${title}`}</title>
                  <meta name="description" content={`${title} by ${responsibility}. Published by ${publisher} on ${publishingDate}.`} />
                  <meta property="og:title" content={`ReadLinker | ${title}`} />
                  <meta property="og:description" content={`${title} by ${responsibility}. Published by ${publisher} on ${publishingDate}.`} />
                  <meta property="og:type" content="book" />
                  <meta property="og:url" content={`https://readlinker.com/book/${rlBookIdNum}`} />
                  <meta property="og:image" content={coverUrl} />
                  <meta property="book:author" content={responsibility} />
                  <meta property="book:isbn" content={isbn} />
                  <meta property="book:release_date" content={publishingDate} />
                </Helmet>

                <Grid container spacing={2} sx={{ height: { xs: 'auto', md: '100%' } }}>
                  {/* Left: book detail panel */}
                  <Grid item xs={12} md={5} sx={{ height: { xs: 'auto', md: '100%' } }}>
                    <Box
                      sx={{
                        height: { xs: 'auto', md: '100%' },
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: { xs: 'visible', md: 'hidden' },
                      }}
                    >
                      {/* header row */}
                      <Box
                        sx={{
                          px: 2, py: 1.5,
                          borderBottom: `1px solid ${TOKENS.border}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 20, fontWeight: 700, color: TOKENS.text }}>書誌詳細</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <IconButton onClick={handleShare} sx={{ ...iconGhostButtonSx, borderRadius: 999, width: 34, height: 34 }}>
                            <IosShareIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Stack>
                      </Box>

                      {/* scroll body */}
                      <Box sx={{ px: 2, py: 2, overflowY: { xs: 'visible', md: 'auto' }, flex: { xs: 'unset', md: 1 }, minHeight: 0 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Card
                              sx={{
                                background: TOKENS.cardBg,
                                border: `1px solid ${TOKENS.border}`,
                                borderRadius: 2,
                                overflow: 'hidden',
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={coverUrl}
                                alt={`Cover for ${title}`}
                                sx={{
                                  objectFit: 'contain',
                                  height: 240,
                                  width: '100%',
                                  padding: '9px',
                                  background: TOKENS.cardBg,
                                }}
                              />
                            </Card>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography
                              variant="h5" component="h1" gutterBottom
                              sx={{ fontSize: 20, lineHeight: 1.2, color: TOKENS.text, letterSpacing: '0.2px' }}
                            >
                              {title}
                            </Typography>

                            {contentDetailTitle && (
                              <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 1 }}>
                                {contentDetailTitle}
                              </Typography>
                            )}

                            {titleOthers && (
                              <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 1.5, whiteSpace: 'pre-wrap' }}>
                                {titleOthers}
                              </Typography>
                            )}

                            <Box
                              sx={{
                                mt: 1,
                                display: 'grid',
                                gridTemplateColumns: '110px 1fr',
                                rowGap: 0.6,
                                columnGap: 1.5,
                                fontSize: 10,
                              }}
                            >
                              <Typography sx={{ color: TOKENS.textSub }}>著者</Typography>
                              <Typography sx={{ color: TOKENS.text }}>
                                {responsibility ?? contributors.map(c => c.name.replace(/,/g, '')).join(', ')}
                              </Typography>

                              <Typography sx={{ color: TOKENS.textSub }}>出版社</Typography>
                              <Typography sx={{ color: TOKENS.text }}>{publisher}</Typography>

                              <Typography sx={{ color: TOKENS.textSub }}>出版日</Typography>
                              <Typography sx={{ color: TOKENS.text }}>{publishingDate}</Typography>

                              <Typography sx={{ color: TOKENS.textSub }}>ISBN</Typography>
                              <Typography sx={{ color: TOKENS.text }}>{isbn}</Typography>
                            </Box>

                            {/* 著者リンク */}
                            {contributors.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1 }}>著者</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  {contributors.filter(c => c.tenkyo_id).map((c) => (
                                    <Stack key={c.tenkyo_id} direction="row" spacing={0.5} alignItems="center">
                                      <Button
                                        href={`/author/${c.tenkyo_id}`}
                                        sx={{
                                          ...ghostButtonSx,
                                          fontSize: 12.5,
                                        }}
                                      >
                                        {c.name.replace(/,/g, '')}
                                      </Button>
                                      {wikipediaLinks[c.tenkyo_id!] && (
                                        <Button
                                          href={wikipediaLinks[c.tenkyo_id!]!}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ ...ghostButtonSx, p: 0.5, minWidth: 'unset', borderRadius: 2, minHeight: 'unset' }}
                                        >
                                          <img src={wikipediaLogo} alt="Wikipedia" style={{ width: 'auto', height: '22px' }} />
                                        </Button>
                                      )}
                                      <Button
                                        href={`https://www.google.com/search?q=${encodeURIComponent(c.name.replace(/,/g, ''))}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ ...ghostButtonSx, p: 0.5, minWidth: 'unset', borderRadius: 2, minHeight: 'unset' }}
                                      >
                                        <img src={googleSearchIcon} alt="Google Search" style={{ width: 'auto', height: '22px' }} />
                                      </Button>
                                    </Stack>
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {/* External links */}
                            <Box sx={{ mt: 1.5 }}>
                              <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1 }}>外部リンク</Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {isbn && (
                                  <Button
                                    href={createAmazonLink(isbn, title)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={logoLinkButtonSx}
                                  >
                                    <img src={amazonImage} alt="Amazonで見る" style={{ width: 'auto', height: '25.5px' }} />
                                  </Button>
                                )}
                                {isbn && (
                                  <Button
                                    href={createCalilLink(isbn)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={logoLinkButtonSx}
                                  >
                                    <img src={calilImage} alt="カーリルで見る" style={{ width: 'auto', height: '25.5px' }} />
                                  </Button>
                                )}
                                {ndlBibId && (
                                  <Button
                                    href={createNDLLink(ndlBibId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={logoLinkButtonSx}
                                  >
                                    <img src={ndlImage} alt="国立国会図書館で見る" style={{ width: 'auto', height: '25.5px' }} />
                                  </Button>
                                )}
                              </Stack>
                            </Box>

                            {/* favorite + list */}
                            <Box
                              sx={{
                                mt: 1.5, pt: 1.5,
                                borderTop: `1px solid ${TOKENS.border}`,
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
                              }}
                            >
                              <IconButton
                                onClick={handleFavoriteToggle}
                                sx={{
                                  ...iconGhostButtonSx,
                                  borderRadius: 999, width: 34, height: 34,
                                  color: isFavorite ? 'rgba(250,204,21,0.95)' : TOKENS.textSub,
                                }}
                              >
                                {isFavorite ? <StarIcon /> : <StarBorderIcon />}
                              </IconButton>

                              <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>
                                {favoriteCount} お気に入り
                              </Typography>

                              <Button variant="outlined" onClick={handleListButtonClick} sx={{ ...ghostButtonSx, ml: { xs: 0, sm: 1 } }}>
                                リストに追加
                              </Button>

                              <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleListClose}
                                PaperProps={{
                                  sx: {
                                    background: TOKENS.glassStrong,
                                    border: `1px solid ${TOKENS.border}`,
                                    color: TOKENS.text,
                                    mt: 1, borderRadius: 2, minWidth: 220,
                                  }
                                }}
                              >
                                {bookLists.map((list) => {
                                  const isInList = inListIds.has(list.user_list_id);
                                  return (
                                    <MenuItem
                                      key={list.user_list_id}
                                      onClick={() => handleToggleListItem(list.user_list_id)}
                                      sx={{
                                        fontSize: 12.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        '&:hover': { background: TOKENS.cardBgHover },
                                      }}
                                    >
                                      {isInList ? (
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e', flexShrink: 0 }} />
                                      ) : (
                                        <Box sx={{ width: 16, flexShrink: 0 }} />
                                      )}
                                      {list.user_list_name}
                                    </MenuItem>
                                  );
                                })}
                                <MenuItem
                                  onClick={handleCreateNewList}
                                  sx={{
                                    fontSize: 12.5,
                                    borderTop: `1px solid ${TOKENS.border}`,
                                    '&:hover': { background: TOKENS.cardBgHover },
                                  }}
                                >
                                  ＋リストを新規作成
                                </MenuItem>
                              </Menu>
                            </Box>

                            {/* 本棚（ログイン時のみ） */}
                            {idToken && (
                              <Box
                                sx={{
                                  mt: 1.5, pt: 1.5,
                                  borderTop: `1px solid ${TOKENS.border}`,
                                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                                  gap: 1, flexWrap: 'wrap',
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 140 }}>
                                  {shelfStatus && shelfStatus.owned_count > 0 ? (
                                    <>
                                      <Button
                                        onClick={() => setShelfStatusExpanded((v) => !v)}
                                        sx={{ ...ghostButtonSx, justifyContent: 'flex-start' }}
                                      >
                                        蔵書数: {shelfStatus.owned_count}
                                      </Button>
                                      {shelfStatusExpanded && (
                                        <Box
                                          sx={{
                                            mt: 1, pl: 1,
                                            display: 'flex', flexDirection: 'column', gap: 0.5,
                                            maxHeight: shelfStatus.shelves.length >= 3 ? 96 : 'none',
                                            overflowY: shelfStatus.shelves.length >= 3 ? 'auto' : 'visible',
                                          }}
                                        >
                                          {shelfStatus.shelves.map((s) => (
                                            <Typography
                                              key={s.global_book_id}
                                              sx={{ fontSize: 12, color: TOKENS.textSub }}
                                            >
                                              {s.shelf_name}
                                            </Typography>
                                          ))}
                                        </Box>
                                      )}
                                    </>
                                  ) : (
                                    <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, lineHeight: '34px' }}>
                                      蔵書なし
                                    </Typography>
                                  )}
                                </Box>

                                <Button variant="outlined" onClick={handleOpenShelfModal} sx={ghostButtonSx}>
                                  本棚へ追加
                                </Button>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Right: 関連書籍パネル（/graph の GetRelatedBooks で取得） */}
                  <Grid item xs={12} md={7} sx={{ height: { xs: 'auto', md: '100%' } }}>
                    <Box
                      sx={{
                        height: { xs: 'auto', md: '100%' },
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: { xs: 'visible', md: 'hidden' },
                        borderLeft: { xs: 'none', md: `1px solid ${TOKENS.border}` },
                        pl: { xs: 0, md: 3 },
                      }}
                    >
                      <Box
                        sx={{
                          px: 2, py: 1.5,
                          borderBottom: `1px solid ${TOKENS.border}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                          関連書籍
                        </Typography>
                        {!relatedLoading && relatedBooks.length > 0 && (
                          <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>
                            {relatedBooksAtLimit
                              ? `${relatedBooks.length}件のみ表示中`
                              : `${relatedBooks.length} 件`}
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          px: 2, py: 2,
                          overflowY: { xs: 'visible', md: 'auto' },
                          WebkitOverflowScrolling: 'touch',
                          flex: { xs: 'unset', md: 1 },
                          minHeight: 0,
                        }}
                      >
                        {relatedLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                          </Box>
                        ) : relatedAuthors.every((a) => a.related_books.length === 0) ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                              関連書籍が見つかりませんでした
                            </Typography>
                          </Box>
                        ) : (
                          // 著者ごとにブロックを分ける（同じ本が複数著者のブロックに重複して出ることを許容する）
                          relatedAuthors
                            .filter((author) => author.related_books.length > 0)
                            .map((author) => (
                              <Box key={author.tenkyo_id} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                  <Button
                                    href={`/author/${author.tenkyo_id}`}
                                    sx={{
                                      ...ghostButtonSx,
                                      fontSize: 12.5,
                                    }}
                                  >
                                    {author.name.replace(/,\s*/g, ' ')}
                                  </Button>
                                  <Typography sx={{ fontSize: 11, color: TOKENS.textSub }}>
                                    {author.related_books.length >= RELATED_BOOKS_LIMIT
                                      ? `${author.related_books.length}件のみ表示中`
                                      : `${author.related_books.length} 件`}
                                  </Typography>
                                </Stack>
                                <Grid container spacing={1.5}>
                                  {author.related_books.map((book) => (
                                    <Grid item xs={6} sm={4} md={4} lg={3} key={`${author.tenkyo_id}-${book.rl_book_id}`}>
                                      <Box>
                                        <Card
                                          onClick={() => {
                                            navigate(`/book/${book.rl_book_id}`);
                                            window.scrollTo(0, 0);
                                          }}
                                          sx={{
                                            transition: '0.25s',
                                            background: TOKENS.cardBg,
                                            border: `1px solid ${TOKENS.border}`,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            boxShadow: 'none',
                                            '&:hover': {
                                              border: `1px solid ${TOKENS.accent}`,
                                              background: TOKENS.cardBgHover,
                                            },
                                          }}
                                        >
                                          <CardMedia
                                            component="img"
                                            image={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
                                            alt={titleWithVolume(book.title, book.volume)}
                                            sx={{
                                              objectFit: 'contain',
                                              height: { xs: 140, sm: 170 },
                                              width: '100%',
                                              padding: '8px',
                                              background: TOKENS.cardBg,
                                            }}
                                          />
                                        </Card>
                                        <Box sx={{ mt: 0.75, px: 0.25 }}>
                                          <Typography
                                            sx={{
                                              fontSize: 12,
                                              color: TOKENS.text,
                                              lineHeight: 1.25,
                                              display: '-webkit-box',
                                              WebkitBoxOrient: 'vertical',
                                              WebkitLineClamp: 2,
                                              overflow: 'hidden',
                                            }}
                                          >
                                            {titleWithVolume(book.title, book.volume)}
                                          </Typography>
                                          {book.pubYear && (
                                            <Typography sx={{ fontSize: 11, color: TOKENS.textSub, mt: 0.25 }}>
                                              {book.pubYear}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            ))
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}

        <Dialog
          open={openCreateListDialog}
          onClose={handleCloseCreateListDialog}
          PaperProps={{
            sx: {
              background: TOKENS.glassStrong,
              border: `1px solid ${TOKENS.border}`,
              color: TOKENS.text,
              borderRadius: 2,
              backdropFilter: 'blur(6px)',
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${TOKENS.border}` }}>
            新しいリストを作成
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              autoFocus margin="dense" label="リスト名" type="text" fullWidth variant="outlined"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              InputLabelProps={{ sx: { color: TOKENS.textSub } }}
              InputProps={{
                sx: {
                  color: TOKENS.text, background: TOKENS.cardBg, borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                }
              }}
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel sx={{ color: TOKENS.textSub }}>公開範囲</InputLabel>
              <Select
                value={newListOpenLevel}
                label="公開範囲"
                onChange={(e) => setNewListOpenLevel(e.target.value as ListOpenLevel)}
                sx={{
                  color: TOKENS.text, background: TOKENS.cardBg, borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                }}
                MenuProps={{ PaperProps: { sx: { background: TOKENS.glassStrong, border: `1px solid ${TOKENS.border}`, color: TOKENS.text, borderRadius: 2 } } }}
              >
                <MenuItem value="public" sx={{ fontSize: 12.5 }}>公開</MenuItem>
                <MenuItem value="url_share" sx={{ fontSize: 12.5 }}>リンク共有</MenuItem>
                <MenuItem value="friends" sx={{ fontSize: 12.5 }}>友達のみ</MenuItem>
                <MenuItem value="private" sx={{ fontSize: 12.5 }}>非公開</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>

          <DialogActions sx={{ borderTop: `1px solid ${TOKENS.border}`, p: 2 }}>
            <Button onClick={handleCloseCreateListDialog} disabled={isCreatingList} sx={ghostButtonSx}>キャンセル</Button>
            <Button onClick={handleCreateList} disabled={isCreatingList || !newListName.trim()} sx={ghostButtonSx}>
              {isCreatingList ? '作成中...' : '作成'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={shelfModalOpen}
          onClose={handleCloseShelfModal}
          PaperProps={{
            sx: {
              background: TOKENS.glassStrong,
              border: `1px solid ${TOKENS.border}`,
              color: TOKENS.text,
              borderRadius: 2,
              backdropFilter: 'blur(6px)',
              minWidth: { xs: '85vw', sm: 420 },
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${TOKENS.border}` }}>
            本棚へ追加
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2.5 }}>
              {coverUrl && (
                <Box
                  component="img"
                  src={coverUrl}
                  alt={title}
                  sx={{ width: 52, height: 'auto', borderRadius: 1, background: TOKENS.cardBg, flexShrink: 0 }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: TOKENS.text, lineHeight: 1.4 }}>
                  {title}
                </Typography>
                {responsibility && (
                  <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.5 }}>
                    {responsibility}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              {shelfStatus && shelfStatus.owned_count > 0 ? (
                <>
                  <Button
                    onClick={() => setShelfModalStatusExpanded((v) => !v)}
                    sx={{ ...ghostButtonSx, justifyContent: 'flex-start' }}
                  >
                    蔵書数: {shelfStatus.owned_count}
                  </Button>
                  {shelfModalStatusExpanded && (
                    <Box
                      sx={{
                        mt: 1, pl: 1,
                        display: 'flex', flexDirection: 'column', gap: 0.5,
                        maxHeight: shelfStatus.shelves.length >= 3 ? 96 : 'none',
                        overflowY: shelfStatus.shelves.length >= 3 ? 'auto' : 'visible',
                      }}
                    >
                      {shelfStatus.shelves.map((s) => (
                        <Typography
                          key={s.global_book_id}
                          sx={{ fontSize: 12, color: TOKENS.textSub }}
                        >
                          {s.shelf_name}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, lineHeight: '34px' }}>
                  蔵書なし
                </Typography>
              )}
            </Box>

            <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 0.5 }}>
              追加先の本棚:
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedShelfIdForAdd}
                onChange={(e) => setSelectedShelfIdForAdd(e.target.value as string)}
                displayEmpty
                sx={{
                  color: TOKENS.text, background: TOKENS.cardBg, borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                }}
                MenuProps={{ PaperProps: { sx: { background: TOKENS.glassStrong, border: `1px solid ${TOKENS.border}`, color: TOKENS.text, borderRadius: 2 } } }}
              >
                <MenuItem value="" sx={{ fontSize: 12.5 }}>本棚を選ばない</MenuItem>
                {allShelves.map((s) => (
                  <MenuItem key={s.shelf_id} value={s.shelf_id} sx={{ fontSize: 12.5 }}>
                    {s.shelf_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>

          <DialogActions sx={{ borderTop: `1px solid ${TOKENS.border}`, p: 2 }}>
            <Button onClick={handleCloseShelfModal} disabled={addingToShelf} sx={ghostButtonSx}>
              キャンセル
            </Button>
            <Button onClick={handleAddToShelf} disabled={addingToShelf} sx={ghostButtonSx}>
              {addingToShelf ? '追加中...' : '本棚に追加'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          ContentProps={{
            sx: {
              background: TOKENS.glassStrong,
              border: `1px solid ${TOKENS.border}`,
              color: TOKENS.text,
              borderRadius: 999,
              backdropFilter: 'blur(6px)',
              fontSize: 12.5,
            }
          }}
        />
      </Container>
    </Box>
    </Box>
  );
}

export default ManifestationDetailPage;
